import { ColumnDef } from '../interface';

export type SortBy = { field: string; direction: 'desc' | 'asc'; columnDef: ColumnDef } | Record<string, never>;

export function sortRowData<T>(rowData: T[], sortBy: SortBy): T[] {
  if (Object.keys(sortBy).length === 0) return rowData;

  const sortedRowData = [...rowData];

  sortedRowData.sort((a, b) => {
    const getValue = (data: T) =>
      data[sortBy.field] ||
      (typeof sortBy.columnDef?.valueGetter === 'function' && sortBy.columnDef.valueGetter({ data })) ||
      null;
    let valueA: string | number = getValue(a);
    let valueB: string | number = getValue(b);

    valueA = !isNaN(Number(valueA)) ? Number(valueA) : valueA;
    valueB = !isNaN(Number(valueB)) ? Number(valueB) : valueB;

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      if (valueA === valueB) return 0;
      return sortBy.direction === 'asc' ? valueA - valueB : valueB - valueA;
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortBy.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    } else if (valueA !== null && valueB === null) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortedRowData;
}

export const getCellClassname = (
  column: ColumnDef,
  { data, index, value }: { data?: any; index: number; value: any },
) => {
  const cellClassname =
    typeof column.cellClass === 'function'
      ? column.cellClass({ data, index, value })
      : column.cellClass
        ? column.cellClass
        : '';

  return cellClassname;
};

export const getRowValue = <T,>(column: ColumnDef<T>, index: number, data: T): string | number | null => {
  const value: string | number =
    (typeof column.valueGetter === 'function' && column.valueGetter({ data })) ||
    data[column.field] ||
    column.shouldRender ||
    null;

  const formattedValue =
    value && typeof column.valueFormatter === 'function'
      ? column.valueFormatter({ data, index, value })
      : value
        ? value
        : null;

  return formattedValue;
};

export const getCellRendererRowValue = <T,>(
  column: ColumnDef<T>,
  index: number,
  data: T,
): string | number | React.ReactElement => {
  const value = getRowValue(column, index, data);

  const cellRenderer =
    value && typeof column.cellRenderer === 'function' ? column.cellRenderer({ data, index, value }) : value;

  return cellRenderer || '—';
};
